(function () {
  'use strict';

  class Viewer360 {
    constructor(el) {
      this.el = el;
      this.frames = Array.from(el.querySelectorAll('.product__360-frame'));
      this.frameCount = this.frames.length;
      if (this.frameCount < 2) return;

      const parentSlide = el.closest('.product__media-item--360');
      if (parentSlide && parentSlide.classList.contains('swiper-slide-duplicate')) return;

      this.activeFrame = 0;
      this.dragStartFrame = 0;
      this.totalMovementX = 0;
      this.isDragging = false;
      this.hasInteracted = false;
      this.framesLoaded = false;
      this.rafId = null;
      this.pendingFrame = null;
      this.demoRafId = null;
      this.demoStarted = false;
      this.fullRotationPx = this.el.offsetWidth || 400;

      this._bindEvents();
      this._setupLazyLoad();
      this._setupSlideObserver();
    }

    // ─── Events ────────────────────────────────────────────────────────────────

    _bindEvents() {
      // Clicks land on the parent container (the viewer div has no hit area due to
      // absolute positioning), so we listen there. The container is the correct
      // interaction area — it matches the viewer's visual footprint exactly.
      const hitTarget = this.el.parentElement || this.el;

      this._onDown = this._onPointerDown.bind(this);
      hitTarget.addEventListener('pointerdown', this._onDown);

      // Prevent Swiper from stealing touchstart on this element
      hitTarget.addEventListener('touchstart', (e) => e.stopPropagation(), { passive: true });

      this._docMove = this._onPointerMove.bind(this);
      this._docUp   = this._onPointerUp.bind(this);

      this._onLockChange = this._onPointerLockChange.bind(this);
      document.addEventListener('pointerlockchange', this._onLockChange);
    }

    _onPointerDown(e) {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      e.stopPropagation();
      e.preventDefault();

      if (this.demoRafId) {
        cancelAnimationFrame(this.demoRafId);
        this.demoRafId = null;
      }

      this.isDragging = true;
      this.totalMovementX = 0;
      this.dragStartFrame = this.activeFrame;

      if (!this.hasInteracted) {
        this.hasInteracted = true;
        this.el.classList.add('has-interacted');
        this._loadAllFrames();
      }

      document.addEventListener('pointermove', this._docMove);
      document.addEventListener('pointerup',   this._docUp);
      document.addEventListener('pointercancel', this._docUp);
      this.el.classList.add('is-dragging');

      // Lock pointer for immersive rotation on mouse
      if (e.pointerType === 'mouse' && this.el.requestPointerLock) {
        this.el.requestPointerLock().catch(() => {});
      }
    }

    _onPointerMove(e) {
      if (!this.isDragging) return;

      this.totalMovementX += e.movementX;
      const pxPerFrame = Math.min(this.fullRotationPx / this.frameCount, 50);
      const frameDelta = Math.round(this.totalMovementX / pxPerFrame);
      const target =
        ((this.dragStartFrame - frameDelta) % this.frameCount + this.frameCount) %
        this.frameCount;

      if (target !== this.pendingFrame) {
        this.pendingFrame = target;
        if (this.rafId) cancelAnimationFrame(this.rafId);
        const frameToShow = target;
        this.rafId = requestAnimationFrame(() => {
          this._showFrame(frameToShow);
          this.rafId = null;
        });
      }
    }

    _onPointerUp() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.el.classList.remove('is-dragging');
      this.el.classList.remove('is-locked');

      document.removeEventListener('pointermove', this._docMove);
      document.removeEventListener('pointerup',   this._docUp);
      document.removeEventListener('pointercancel', this._docUp);

      if (this.rafId) { cancelAnimationFrame(this.rafId); this.rafId = null; }

      if (this.pendingFrame !== null) {
        this._showFrame(this.pendingFrame);
        this.pendingFrame = null;
      }

      if (document.pointerLockElement === this.el) document.exitPointerLock();
    }

    _onPointerLockChange() {
      if (document.pointerLockElement === this.el) {
        this.el.classList.add('is-locked');
      } else {
        this.el.classList.remove('is-locked');
        if (this.isDragging) this._onPointerUp();
      }
    }

    // ─── Lazy load ─────────────────────────────────────────────────────────────

    _setupLazyLoad() {
      if (!('IntersectionObserver' in window)) {
        this._loadAllFrames();
        this._startDemoAnimation();
        return;
      }
      const io = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            this._loadAllFrames();
            this._startDemoAnimation();
            io.disconnect();
          }
        },
        { rootMargin: '200px' }
      );
      io.observe(this.el);
    }

    _loadAllFrames() {
      if (this.framesLoaded) return;
      this.framesLoaded = true;
      this.frames.forEach((frame, i) => {
        if (i === 0 || !frame.dataset.src) return;
        frame.src = frame.dataset.src;
        delete frame.dataset.src;
      });
    }

    // ─── Demo animation ────────────────────────────────────────────────────────

    _startDemoAnimation() {
      if (this.hasInteracted || this.demoStarted) return;
      this.demoStarted = true;

      const deadline = performance.now() + 8000;
      const startWhenReady = () => {
        if (this.hasInteracted) return;
        const frame1 = this.frames[1];
        const ready = frame1 && frame1.complete && frame1.naturalWidth > 1;
        if (!ready && performance.now() < deadline) {
          this.demoRafId = requestAnimationFrame(startWhenReady);
          return;
        }
        this._runDemoAnimation();
      };
      this.demoRafId = requestAnimationFrame(startWhenReady);
    }

    _runDemoAnimation() {
      const durationMs = 3800;
      const rotations = 2;
      const startTime = performance.now();
      const ease = (t) =>
        t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

      const tick = (now) => {
        if (this.hasInteracted) return;
        const progress = Math.min((now - startTime) / durationMs, 1);
        const frameIndex =
          Math.floor(ease(progress) * this.frameCount * rotations) % this.frameCount;
        this._showFrame(frameIndex);
        if (progress < 1) {
          this.demoRafId = requestAnimationFrame(tick);
        } else {
          this._showFrame(0);
          this.demoRafId = null;
        }
      };
      this.demoRafId = requestAnimationFrame(tick);
    }

    // ─── Slide observer ────────────────────────────────────────────────────────

    _setupSlideObserver() {
      const slideEl = this.el.closest('.product__media-item--360');
      const galleryEl = this.el.closest('media-gallery');
      if (!slideEl || !galleryEl) return;

      const viewerEl = galleryEl.querySelector('[id^="GalleryViewer"]');

      const update = () => {
        const isActive =
          slideEl.classList.contains('swiper-slide-active') &&
          !slideEl.classList.contains('swiper-slide-duplicate');

        galleryEl.classList.toggle('product__360-is-active', isActive);

        const swiper = viewerEl && viewerEl.swiper;
        if (swiper) {
          swiper.allowTouchMove = !isActive;
          if (isActive && swiper.thumbs && swiper.thumbs.swiper) {
            swiper.thumbs.swiper.slideTo(swiper.realIndex);
          }
        }

        if (isActive) {
          this._loadAllFrames();
          this._startDemoAnimation();
        }
      };

      new MutationObserver(update).observe(slideEl, {
        attributes: true,
        attributeFilter: ['class'],
      });

      update();
    }

    // ─── Frame display ─────────────────────────────────────────────────────────

    _showFrame(index) {
      if (index === this.activeFrame) return;
      const frame = this.frames[index];
      if (!frame || !frame.complete || frame.naturalWidth <= 1) return;
      this.frames[this.activeFrame].classList.remove('product__360-frame--active');
      frame.classList.add('product__360-frame--active');
      this.activeFrame = index;
    }
  }

  // ─── Init ──────────────────────────────────────────────────────────────────

  function initViewers() {
    document.querySelectorAll('[data-360-viewer]').forEach((el) => {
      if (el._viewer360Initialized) return;
      el._viewer360Initialized = true;
      new Viewer360(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(initViewers, 0));
  } else {
    setTimeout(initViewers, 0);
  }

  document.addEventListener('shopify:section:load', () => setTimeout(initViewers, 0));
})();
